import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { isApiError } from '@/lib/apiError';

import { protocoloFormSchema, type ProtocoloFormValues } from '../schemas';

import { EstablecimientoFieldWithCreate } from './EstablecimientoFieldWithCreate';
import { ProfesionalFieldWithCreate } from './ProfesionalFieldWithCreate';

interface ProtocoloFormProps {
  onSubmit: (values: ProtocoloFormValues) => void;
  onCancel: () => void;
  submitting: boolean;
  error?: unknown;
}

function buildErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (isApiError(error) && error.isValidationError()) {
    return error.validationErrors().map((e) => `${e.field}: ${e.message}`).join('. ');
  }
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return null;
}

export function ProtocoloForm({
  onSubmit,
  onCancel,
  submitting,
  error,
}: ProtocoloFormProps): JSX.Element {
  const today = new Date();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<ProtocoloFormValues>({
    resolver: zodResolver(protocoloFormSchema),
    defaultValues: {
      fecha_recepcion: today,
      fecha_protocolo: today,
      uid_profesional: '',
      uid_establecimiento: '',
    },
  });

  const uidProfesional = watch('uid_profesional');

  useEffect(() => {
    setValue('uid_establecimiento', '');
  }, [uidProfesional, setValue]);
  const errorMessage = buildErrorMessage(error);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="fecha_recepcion"
            render={({ field, fieldState }) => (
              <DatePicker
                label="Fecha de recepción"
                value={field.value}
                onChange={(date) => field.onChange(date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message ?? ' ',
                  },
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="fecha_protocolo"
            render={({ field, fieldState }) => (
              <DatePicker
                label="Fecha del protocolo"
                value={field.value}
                onChange={(date) => field.onChange(date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message ?? ' ',
                  },
                }}
              />
            )}
          />
        </Stack>

        <Controller
          control={control}
          name="uid_profesional"
          render={({ field, fieldState }) => (
            <ProfesionalFieldWithCreate
              value={field.value}
              onChange={(uid) => field.onChange(uid)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? ' '}
            />
          )}
        />

        <Controller
          control={control}
          name="uid_establecimiento"
          render={({ field, fieldState }) => (
            <EstablecimientoFieldWithCreate
              value={field.value}
              onChange={(uid) => field.onChange(uid)}
              uidProfesional={uidProfesional}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? ' '}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !isDirty}
          >
            {submitting ? 'Guardando...' : 'Crear protocolo'}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}
